import { QueryClient } from '@tanstack/react-query';
import { useNotificationStore } from 'Modules/notification/store';
import {
  ANALYTICS_EVENT_SOFTPHONE,
  RECONNECT_DELAY_MS,
} from 'Modules/softphone/constants';
import {
  fetchSipConfig,
  QUERY_KEY_FETCH_SIP_CONFIG,
} from 'Modules/softphone/requests/index.requests';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { CallManager } from 'Modules/softphone/services/CallManager';
import { DeviceService } from 'Modules/softphone/services/DeviceService';
import { HeartbeatService } from 'Modules/softphone/services/HeartbeatService';
import { NetworkMonitorService } from 'Modules/softphone/services/NetworkMonitorService';
import { SipService } from 'Modules/softphone/services/SipService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import type { SoftphoneStore } from 'Modules/softphone/store';
import { useSoftphoneStore } from 'Modules/softphone/store';
import {
  getPersistedMicrophone,
  getPersistedRinger,
  getPersistedSpeaker,
  getPersistedSipConfig,
  setPersistedSipConfig,
} from 'Modules/softphone/store/storage';
import type { PermissionState, SipConfig } from 'Modules/softphone/types';
import {
  lifecycleLogger as logger,
  recoveryLogger,
} from 'Modules/softphone/utils/logger';
import type { SoftphoneCoordinator } from 'Modules/softphone/utils/softphoneCoordinator';
import { RegistererState } from 'sip.js';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { retryWithBackoff } from 'Utils/exponentialBackoff';
import {
  RECONNECT_RETRY_CONFIG,
  CLEANUP_WAIT_DELAY_MS,
  CONNECTION_LOSS_FEEDBACK_DELAY_MS,
  CALL_DROP_TIMEOUT_MS,
  type ReconnectRetryOptions,
} from './index.constants';

/**
 * Orchestrates softphone initialization, registration, and cleanup.
 * Facade that coordinates SipService, DeviceService, WebRTCService, and AudioService.
 *
 * Singleton pattern - manages its own state:
 * - isConfigured: Set once via configure(), prevents re-configuration
 * - isInitialized: Set after successful SIP initialization
 */
export class ConnectionManager {
  private static instance: ConnectionManager;

  private sipService: SipService;
  private deviceService: DeviceService;
  private webrtcService: WebRTCService;
  private audioService: AudioService;
  private callManager: CallManager;
  private heartbeatService: HeartbeatService;
  private networkMonitor: NetworkMonitorService;

  private isInitialized = false;
  private isConfigured = false;
  private store: SoftphoneStore | null = null;
  private coordinator: SoftphoneCoordinator | null = null;
  private rootStoreProps: RootStoreProps | null = null;
  private reconnectInProgress = false;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private connectionLossFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
  private callDropTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeActiveCalls: (() => void) | null = null;

  private recoveryAttempts = 0;
  private maxRecoveryAttempts = 10;
  private recoveryState: 'idle' | 'recovering' = 'idle';
  private cleanupPromise: Promise<void> | null = null;

  private constructor() {
    this.sipService = SipService.getInstance();
    this.deviceService = DeviceService.getInstance();
    this.webrtcService = WebRTCService.getInstance();
    this.audioService = AudioService.getInstance();
    this.callManager = CallManager.getInstance();
    this.heartbeatService = HeartbeatService.getInstance();
    this.networkMonitor = NetworkMonitorService.getInstance();

    logger.info('Initialized');
  }

  static getInstance(): ConnectionManager {
    if (!ConnectionManager.instance) {
      ConnectionManager.instance = new ConnectionManager();
    }
    return ConnectionManager.instance;
  }

  /**
   * Check if the manager is configured
   */
  getIsConfigured(): boolean {
    return this.isConfigured;
  }

  /**
   * Check if the manager is initialized
   */
  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Configure the manager with store and coordinator (Dependency Injection)
   * Must be called before initialization. Can only be called once.
   */
  configure(
    store: SoftphoneStore,
    coordinator: SoftphoneCoordinator,
    rootStoreProps?: RootStoreProps
  ): void {
    if (this.isConfigured) {
      logger.warn('ConnectionManager already configured, skipping');
      return;
    }
    this.store = store;
    this.coordinator = coordinator;
    this.rootStoreProps = rootStoreProps || null;
    this.isConfigured = true;

    this.sipService.setOnTransportConnect(() => {
      this.clearConnectionLossFeedback();
    });

    logger.debug('Configured with store and coordinator');
  }

  /**
   * Initialize SIP connection
   */
  async initializeSip(
    config: SipConfig,
    forceReinit = false
  ): Promise<boolean> {
    // Wait for any in-flight cleanup to finish before initializing
    if (this.cleanupPromise !== null) {
      logger.debug('Waiting for pending cleanup to complete before init...');
      await this.cleanupPromise;
    }

    if (!this.store || !this.coordinator) {
      logger.error('Manager not configured - call configure() first');
      return false;
    }

    // CRITICAL: Prevent re-initialization if already initialized
    // This prevents multiple UserAgent/Registerer instances
    // forceReinit allows reconnection flows to bypass this check
    if (this.isInitialized && !forceReinit) {
      logger.warn(
        'Already initialized, skipping re-initialization. Use reconnect() to re-establish connection.'
      );
      return true;
    }

    try {
      // Request activation through coordinator
      const canActivate = this.coordinator.requestActivation('new');
      if (!canActivate) {
        logger.error('Cannot activate - coordinator blocked');
        this.store.setStatus('failed');
        this.store.setError(
          new Error(
            'Cannot activate new softphone - legacy softphone may be active'
          )
        );
        return false;
      }

      logger.debug('Setting status to connecting...');
      this.store.setStatus('connecting');
      this.store.setConfig(config);

      logger.debug('Initializing SipService...');
      await this.sipService.initialize(config);
      logger.debug('SipService initialized successfully');

      logger.debug('Registering with SIP server...');
      await this.sipService.register();
      logger.info('Registration successful!');

      // Update status to registered
      this.store.setStatus('registered');
      this.store.resetRegistrationAttempts();
      this.store.setLastRegistrationTime(new Date().toISOString());
      this.store.setError(null);
      this.clearConnectionLossFeedback();

      // Persist config for reconnection fallback
      // This provides resilience when store/cache is unavailable
      void setPersistedSipConfig(config);

      // Initialize CallManager subscriptions (ringback audio, permissions sync)
      // This is done here to ensure proper initialization order:
      // 1. SIP registered → 2. CallManager ready for calls
      this.callManager.initialize(this.rootStoreProps || undefined);

      // Start heartbeat monitoring (checks connection every 15s)
      // Based on legacy PhoneStore.setSIPKeepAlive()
      this.heartbeatService.start(this.sipService, () => {
        const hasActiveCalls = useSoftphoneStore.getState().activeCalls.size > 0;
        if (hasActiveCalls) {
          logger.warn(
            'Heartbeat: disconnection during active call — using transport-only reconnect to preserve session'
          );
          void this.reconnectTransportOnly();
        } else {
          logger.warn(
            'Heartbeat detected disconnection, attempting reconnect with retry'
          );
          void this.reconnectWithRetry();
        }
      });

      // Start network monitoring (detects offline/online events)
      // Based on legacy PhoneStore.initializeNetworkListener()
      this.networkMonitor.initialize(
        () => this.handleNetworkLossWithAlert(),
        () => void this.handleNetworkRestoredWithReconnect()
      );

      this.setupActiveCallsSubscription();

      this.isInitialized = true;
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('SIP initialization failed', error);
      this.store.setStatus('failed');
      this.store.setError(error);
      this.store.incrementRegistrationAttempts();

      // Deactivate on error
      this.coordinator.deactivate('new');
      return false;
    }
  }

  /**
   * Initialize audio devices
   *
   * Loads persisted device preferences from localforage (shared with legacy PhoneStore).
   * Falls back to first available device if persisted device is not found.
   */
  async initializeDevices(): Promise<boolean> {
    if (!this.store) {
      logger.error('Manager not configured - call configure() first');
      return false;
    }

    try {
      // Read the permission instead of requesting it: getUserMedia here would
      // open the microphone on every page load. The request is deferred to
      // ensureMicrophoneAccess().
      logger.debug('Reading microphone permission (no prompt)...');
      const permission = await this.deviceService.getPermissionState();
      logger.debug(`Permission state: ${permission}`);
      this.store.setPermissionState(permission);

      if (permission !== 'granted') {
        logger.info(
          'Microphone permission not granted yet - deferring request until the user places/answers a call or opens audio settings'
        );
        return false;
      }

      logger.debug('Enumerating audio devices...');
      const microphones = await this.deviceService.getAudioInputDevices();
      const speakers = await this.deviceService.getAudioOutputDevices();

      logger.debug('Found devices', {
        microphones: microphones.length,
        speakers: speakers.length,
      });

      this.store.setMicrophones(microphones);
      this.store.setSpeakers(speakers);
      this.store.setRingers(speakers);

      // Load persisted preferences (shared with legacy PhoneStore via localforage)
      const [persistedMic, persistedSpeaker, persistedRinger] =
        await Promise.all([
          getPersistedMicrophone(),
          getPersistedSpeaker(),
          getPersistedRinger(),
        ]);

      logger.debug('Loaded persisted preferences', {
        persistedMic,
        persistedSpeaker,
        persistedRinger,
      });

      // Set microphone - use persisted if available and device exists, else use first
      if (microphones.length > 0) {
        const micDeviceExists =
          persistedMic && microphones.some((m) => m.deviceId === persistedMic);
        const selectedMic = micDeviceExists
          ? persistedMic
          : microphones[0].deviceId;
        const selectedMicLabel =
          microphones.find((m) => m.deviceId === selectedMic)?.label ||
          selectedMic;

        logger.debug(
          `Setting microphone: ${selectedMicLabel} (persisted: ${String(
            micDeviceExists
          )})`
        );
        this.store.setSelectedMicrophone(selectedMic);
      }

      // Set speaker - use persisted if available and device exists, else use first
      if (speakers.length > 0) {
        const speakerDeviceExists =
          persistedSpeaker &&
          speakers.some((s) => s.deviceId === persistedSpeaker);
        const selectedSpeaker = speakerDeviceExists
          ? persistedSpeaker
          : speakers[0].deviceId;
        const selectedSpeakerLabel =
          speakers.find((s) => s.deviceId === selectedSpeaker)?.label ||
          selectedSpeaker;

        logger.debug(
          `Setting speaker: ${selectedSpeakerLabel} (persisted: ${String(
            speakerDeviceExists
          )})`
        );
        this.store.setSelectedSpeaker(selectedSpeaker);

        // Also set in AudioService for consistency
        await this.audioService.setSpeaker(selectedSpeaker);
      }

      // Set ringer - use persisted if available and device exists, else use speaker or first
      if (speakers.length > 0) {
        const ringerDeviceExists =
          persistedRinger &&
          speakers.some((s) => s.deviceId === persistedRinger);
        // Fallback: use persisted speaker, then first device
        const fallbackRinger = persistedSpeaker || speakers[0].deviceId;
        const selectedRinger = ringerDeviceExists
          ? persistedRinger
          : fallbackRinger;
        const selectedRingerLabel =
          speakers.find((s) => s.deviceId === selectedRinger)?.label ||
          selectedRinger;

        logger.debug(
          `Setting ringer: ${selectedRingerLabel} (persisted: ${String(
            ringerDeviceExists
          )})`
        );
        this.store.setSelectedRinger(selectedRinger);

        await this.audioService.setRinger(selectedRinger);
      }

      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Device initialization failed', error);
      return false;
    }
  }

  /**
   * Ensure the microphone is usable before a call needs it. Prompts only when
   * the permission is undecided, and enumerates devices when access is granted
   * after startup. Notifies the user when denied.
   *
   * @param callType - Drives the denied-permission notification copy
   * @returns true when the microphone can be used
   */
  async ensureMicrophoneAccess(
    callType: 'incoming' | 'outgoing' | null = null
  ): Promise<boolean> {
    const currentState = await this.deviceService.getPermissionState();
    const hasDevices = useSoftphoneStore.getState().microphones.length > 0;
    let permission: PermissionState = currentState;

    // Also prompt when no device is enumerable yet: ids stay hidden until a
    // stream has been opened once, which getPermissionState() cannot detect
    // (notably in Electron, where it always reports 'granted').
    if (currentState !== 'granted' || !hasDevices) {
      permission = await this.deviceService.requestPermissions();
    }

    this.store?.setPermissionState(permission);

    if (permission !== 'granted') {
      logger.warn('Microphone access denied', { callType });
      this.notifyMicrophoneDenied(callType);
      return false;
    }

    if (!hasDevices) {
      await this.initializeDevices();
    }

    return true;
  }

  /**
   * Notify the user that microphone access is blocked.
   * @private
   */
  private notifyMicrophoneDenied(
    callType: 'incoming' | 'outgoing' | null
  ): void {
    const copy = {
      incoming: 'Please enable microphone access in your browser settings to receive calls.',
      outgoing: 'Please enable microphone access in your browser settings to make calls.',
      default:
        'Please enable microphone access in your browser settings to make and receive calls.',
    }[callType || 'default'];

    useNotificationStore.getState().addNotify({
      title: 'Allow access to your microphone',
      message: copy,
      type: 'info',
      autoDismiss: true,
      dismissible: 'button',
    });
  }

  /**
   * Setup incoming call handler
   * Note: Incoming calls are handled by legacy softphone
   */
  setupIncomingCallHandler(): () => void {
    return () => {};
  }

  /**
   * Register with SIP server
   *
   * If UserAgent is already initialized, just re-registers.
   * Otherwise, triggers full initialization if config is available.
   */
  async register(): Promise<void> {
    if (!this.store) {
      throw new Error('Manager not configured - call configure() first');
    }

    // If already registered, nothing to do
    if (useSoftphoneStore.getState().status === 'registered') {
      logger.debug('Already registered, skipping');
      return;
    }

    // If UserAgent exists (initialized but unregistered), just register
    if (this.sipService.getUserAgent()) {
      logger.debug('UserAgent exists, re-registering...');
      this.store.setStatus('registering');

      try {
        await this.sipService.register();
        this.store.setStatus('registered');
        this.store.setError(null);
        this.store.resetRegistrationAttempts();
        this.store.setLastRegistrationTime(new Date().toISOString());
        this.clearConnectionLossFeedback();
        logger.info('Re-registration successful');
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Re-registration failed', error);
        this.store.setStatus('failed');
        this.store.setError(error);
        this.store.incrementRegistrationAttempts();
        throw error;
      }
      return;
    }

    // No UserAgent - need full initialization
    const config = useSoftphoneStore.getState().config;
    if (!config) {
      const error = new Error('Cannot register: SIP config not available');
      logger.error(error.message);
      this.store.setError(error);
      throw error;
    }

    logger.debug('No UserAgent, triggering full initialization...');
    const success = await this.initializeSip(config);
    if (!success) {
      throw useSoftphoneStore.getState().error || new Error('Registration failed');
    }
  }

  /**
   * Unregister from SIP server
   *
   * Keeps WebSocket connection alive for quick re-registration.
   * Use this when user wants to go "offline" temporarily.
   */
  async unregister(): Promise<void> {
    if (!this.store) {
      throw new Error('Manager not configured - call configure() first');
    }

    if (useSoftphoneStore.getState().status !== 'registered') {
      logger.debug('Not registered, skipping unregister');
      return;
    }

    try {
      this.store.setStatus('unregistering');
      await this.sipService.unregister();
      this.store.setStatus('disconnected');
      this.store.setError(null);
      logger.info('Unregistration successful');
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Unregistration failed', error);
      this.store.setError(error);
      throw error;
    }
  }

  /**
   * Attempt to reconnect to SIP server
   *
   * Performs full cleanup and re-initialization.
   * Use this after network issues or when connection is lost.
   *
   * Config Resolution Order (fallback chain):
   * 1. Zustand store (store.config)
   * 2. SipService cache (sipService.getConfig())
   * 3. Persisted storage (localforage) - survives memory pressure/HMR
   *
   * If all sources fail, throws an error. The caller should handle this
   * by triggering a config refetch or notifying the user.
   *
   * Includes debounce logic to prevent multiple simultaneous reconnect attempts.
   */
  async reconnect(): Promise<void> {
    if (!this.store) {
      throw new Error('Manager not configured - call configure() first');
    }

    // Prevent multiple simultaneous reconnect attempts
    if (this.reconnectInProgress) {
      logger.warn('Reconnect already in progress, skipping duplicate attempt');
      return;
    }

    // Clear any pending reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.reconnectInProgress = true;

    try {
      // Config resolution with fallback chain
      const config = await this.resolveConfig();

      if (!config) {
        const error = new Error(
          'Cannot reconnect: SIP config not available. Please refresh the page to re-authenticate.'
        );
        logger.error(error.message);
        this.store.setError(error);
        throw error;
      }

      // Cleanup existing connection
      logger.debug('Cleaning up existing connection...');
      await this.cleanup();

      // Wait a bit before reconnecting to allow cleanup to complete
      await new Promise((resolve) => setTimeout(resolve, RECONNECT_DELAY_MS));

      // Re-initialize
      logger.debug('Re-initializing with config...', {
        uri: config.uri,
        domain: config.domain,
      });

      const success = await this.initializeSip(config);

      if (!success) {
        const error = new Error(
          'Reconnection failed during SIP initialization'
        );
        logger.error(error.message);
        // Status is already set to 'failed' by initializeSip() on error
        this.store.setError(error);
        throw error;
      }

      logger.info('Reconnection successful');
    } catch (err) {
      // Ensure status is set to 'failed' if not already set by initializeSip
      // This handles cases where error occurs before initializeSip is called
      if (this.store && useSoftphoneStore.getState().status === 'connecting') {
        const error = err instanceof Error ? err : new Error(String(err));
        this.store.setStatus('failed');
        this.store.setError(error);
      }
      throw err;
    } finally {
      this.reconnectInProgress = false;
    }
  }

  /**
   * Reconnect with exponential backoff retry logic
   *
   * Enhanced version of reconnect() that mirrors legacy sipConnectionRecoveryService behavior.
   * Wraps the existing reconnect() method with retry logic using exponential backoff.
   *
   * Features:
   * - Prevents duplicate recovery attempts (debounce)
   * - Uses exponential backoff with jitter
   * - Logs each retry attempt
   * - Updates recovery state for UI feedback
   * - Notifies user on complete failure
   * - Cleans up state on success or failure
   */
  async reconnectWithRetry({
    maxAttempts = RECONNECT_RETRY_CONFIG.MAX_ATTEMPTS,
    initialDelay = RECONNECT_RETRY_CONFIG.INITIAL_DELAY,
    maxDelay = RECONNECT_RETRY_CONFIG.MAX_DELAY,
    backoffFactor = RECONNECT_RETRY_CONFIG.BACKOFF_FACTOR,
  }: ReconnectRetryOptions = {}): Promise<void> {
    if (!this.store) {
      throw new Error(
        'ConnectionManager not configured - call configure() first'
      );
    }

    // Prevent duplicate recovery attempts
    if (this.recoveryState === 'recovering') {
      recoveryLogger.warn(
        'Recovery already in progress, skipping duplicate attempt'
      );
      return;
    }

    recoveryLogger.info('Starting reconnection with retry', {
      maxAttempts,
      initialDelay,
      maxDelay,
      backoffFactor,
    });

    this.recoveryState = 'recovering';
    this.recoveryAttempts = 0;
    this.store.setRecovering(true);

    try {
      await retryWithBackoff({
        operation: () => this.reconnect(),
        maxAttempts,
        initialDelay,
        maxDelay,
        backoffFactor,
        jitter: true,
        timeout: 20000,
        shouldRetry: (error: Error) => {
          // Always retry unless explicitly told not to
          recoveryLogger.info('Reconnection attempt failed, will retry', {
            error: error.message,
          });
          return true;
        },
        onRetry: (error: Error, attempt: number, nextDelay: number) => {
          this.recoveryAttempts = attempt + 1;
          recoveryLogger.info('Reconnection retry attempt', {
            attempt: attempt + 1,
            maxAttempts,
            error: error.message,
            nextDelay,
          });

          if (this.store) {
            this.store.setError(error);
          }
        },
      });

      recoveryLogger.info('Reconnection with retry succeeded');
      this.recoveryAttempts = 0;

      if (this.store) {
        this.store.setError(null);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      recoveryLogger.error('Reconnection failed after all retry attempts', err);

      // This prevents the status from being stuck in 'connecting' when recovery fails
      if (this.store) {
        this.store.setStatus('failed');
        this.store.setError(err);
      }

      throw err;
    } finally {
      this.recoveryState = 'idle';
      this.store?.setRecovering(false);
      recoveryLogger.debug('Recovery state set to idle');
    }
  }

  /**
   * Get current recovery state for UI feedback
   *
   * Returns the current state of reconnection recovery attempts.
   * Used by useRecoveryCoordinator hook to provide UI feedback.
   */
  getRecoveryState(): {
    isRecovering: boolean;
    attempts: number;
    maxAttempts: number;
  } {
    return {
      isRecovering: this.recoveryState === 'recovering',
      attempts: this.recoveryAttempts,
      maxAttempts: this.maxRecoveryAttempts,
    };
  }

  /**
   * Reconnect with fresh config fetch from server
   *
   * This method ALWAYS fetches fresh config from the server before reconnecting.
   * Mirrors the legacy PhoneStore.runRegister() flow but using the new softphone module.
   *
   * Legacy flow (sipConnectionRecoveryService.startConfigRecovery):
   * 1. Clear existing config (signedInPersonConfig = null)
   * 2. Call loadConfig() to fetch fresh config from API
   * 3. Call runRegister() which waits for config and initializes SIP
   *
   * New flow (this method):
   * 1. Fetch fresh config directly from API using fetchSipConfig()
   * 2. Cleanup existing connection
   * 3. Store fresh config in Zustand store and persisted storage
   * 4. Re-initialize SIP with forceReinit=true to bypass isInitialized check
   * 5. Includes retry logic with exponential backoff
   *
   * @param options - Retry configuration options (maxAttempts, delays, etc.)
   */
  async reconnectWithConfigRefetch({
    maxAttempts = RECONNECT_RETRY_CONFIG.MAX_ATTEMPTS,
    initialDelay = RECONNECT_RETRY_CONFIG.INITIAL_DELAY,
    maxDelay = RECONNECT_RETRY_CONFIG.MAX_DELAY,
    backoffFactor = RECONNECT_RETRY_CONFIG.BACKOFF_FACTOR,
    enableRetry = true, // NEW: Allow disabling retry for manual reconnects
  }: ReconnectRetryOptions = {}): Promise<void> {
    if (!this.store || !this.rootStoreProps) {
      throw new Error(
        'ConnectionManager not configured - call configure() first'
      );
    }

    // CRITICAL: Set recoveryState IMMEDIATELY to prevent race conditions
    // This must happen BEFORE any async operations to block duplicate calls
    if (this.recoveryState === 'recovering') {
      recoveryLogger.warn(
        'Recovery already in progress, skipping duplicate attempt'
      );
      return;
    }

    // Set state SYNCHRONOUSLY before any await
    this.recoveryState = 'recovering';
    this.recoveryAttempts = 0;
    this.store.setRecovering(true);

    recoveryLogger.info(
      'Starting reconnection with FRESH config fetch (mirrors legacy startConfigRecovery)',
      {
        maxAttempts,
        initialDelay,
        maxDelay,
        backoffFactor,
        enableRetry,
      }
    );

    // Define the reconnection operation
    const reconnectOperation = async () => {
      recoveryLogger.info('Invalidating React Query cache for SIP config...');

      // Invalidate React Query cache to ensure fresh data
      // This prevents using stale credentials from cache
      try {
        const queryClient = new QueryClient();
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FETCH_SIP_CONFIG],
          exact: true,
        });
        recoveryLogger.info('React Query cache invalidated');
      } catch (cacheError) {
        // Non-critical error - continue with cache-busting fetch
        recoveryLogger.warn(
          'Failed to invalidate React Query cache, continuing with cache-busting fetch',
          {
            error:
              cacheError instanceof Error
                ? cacheError.message
                : String(cacheError),
          }
        );
      }

      recoveryLogger.info(
        'Fetching FRESH config directly from API (cache already invalidated)...'
      );

      let sipConfig: SipConfig;
      try {
        // Use softphone's own fetchSipConfig() to get fresh config
        // This mirrors legacy ConfigStore.loadConfig() but returns SipConfig directly
        // Cache was already invalidated above to ensure fresh data
        sipConfig = await fetchSipConfig();

        recoveryLogger.info('Fresh config fetched from API successfully', {
          uri: sipConfig.uri,
          domain: sipConfig.domain,
          authorizationUsername: sipConfig.authorizationUsername,
          passwordLength: sipConfig.authorizationPassword?.length || 0,
        });
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        recoveryLogger.error('❌ Config fetch failed', err);
        throw err;
      }

      recoveryLogger.info('Cleaning up existing connection...');
      await this.cleanup();

      recoveryLogger.info('Waiting for cleanup to complete...');
      await new Promise((resolve) =>
        setTimeout(resolve, CLEANUP_WAIT_DELAY_MS)
      );

      recoveryLogger.info('Storing config in Zustand store');
      if (!this.store) {
        throw new Error('Store not available - cannot set config');
      }
      this.store.setConfig(sipConfig);

      recoveryLogger.info('Persisting config to storage');
      await setPersistedSipConfig(sipConfig);

      recoveryLogger.info(
        'Re-initializing with fresh config (forceReinit=true)',
        {
          uri: sipConfig.uri,
          domain: sipConfig.domain,
          authorizationUsername: sipConfig.authorizationUsername,
          passwordLength: sipConfig.authorizationPassword?.length || 0,
        }
      );

      let success = false;
      try {
        success = await this.initializeSip(sipConfig, true);
        recoveryLogger.info(`initializeSip returned: ${String(success)}`);
      } catch (initError) {
        recoveryLogger.error('❌ initializeSip threw error', {
          error:
            initError instanceof Error ? initError.message : String(initError),
        });
        throw initError;
      }

      if (!success) {
        const error = new Error(
          'Reconnection failed during SIP initialization'
        );
        recoveryLogger.error('SIP initialization failed', error);
        throw error;
      }

      recoveryLogger.info('Reconnection successful!');
    };

    try {
      // Conditionally apply retry logic based on enableRetry flag
      if (enableRetry) {
        recoveryLogger.info('Using automatic retry with backoff');
        await retryWithBackoff({
          operation: reconnectOperation,
          maxAttempts,
          initialDelay,
          maxDelay,
          backoffFactor,
          jitter: true,
          timeout: 20000,
          shouldRetry: (error: Error) => {
            recoveryLogger.info('Reconnection attempt failed, will retry', {
              error: error.message,
            });
            return true;
          },
          onRetry: (error: Error, attempt: number, nextDelay: number) => {
            this.recoveryAttempts = attempt + 1;

            recoveryLogger.info('Reconnection retry attempt', {
              attempt: attempt + 1,
              maxAttempts,
              error: error.message,
              nextDelay,
            });

            // Update store for UI feedback
            if (this.store) {
              this.store.setError(error);
            }
          },
        });
      } else {
        recoveryLogger.info('Manual reconnect - no automatic retry');
        await reconnectOperation();
      }

      recoveryLogger.info('Reconnection succeeded');
      this.recoveryAttempts = 0;

      if (this.store) {
        this.store.setError(null);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      recoveryLogger.error(
        enableRetry
          ? 'Reconnection failed after all retry attempts'
          : 'Manual reconnection failed',
        err
      );

      if (this.store) {
        this.store.setStatus('failed');
        this.store.setError(err);
      }

      throw err;
    } finally {
      this.recoveryState = 'idle';
      this.store?.setRecovering(false);
      recoveryLogger.debug('Recovery state set to idle');
    }
  }

  /**
   * Resolve SIP config from multiple sources (fallback chain).
   *
   * Resolution order:
   * 1. Zustand store - fastest, in-memory
   * 2. SipService cache - singleton, survives React remounts
   * 3. Persisted storage - survives memory pressure, HMR, browser idle
   *
   * @returns SipConfig or null if not available from any source
   */
  private async resolveConfig(): Promise<SipConfig | null> {
    const storeConfig = useSoftphoneStore.getState().config;
    if (storeConfig) {
      logger.debug('Config resolved from Zustand store');
      return storeConfig;
    }

    const sipServiceConfig = this.sipService.getConfig();
    if (sipServiceConfig) {
      logger.info('Config resolved from SipService cache');
      this.store?.setConfig(sipServiceConfig);
      return sipServiceConfig;
    }

    logger.warn(
      'Config not in store or SipService cache, trying persisted storage...'
    );
    const persistedConfig = await getPersistedSipConfig();
    if (persistedConfig) {
      logger.info('Config resolved from persisted storage (localforage)');
      // Sync back to store
      this.store?.setConfig(persistedConfig);
      return persistedConfig;
    }

    logger.error('Config not available from any source', {
      storeConfig: !!this.store?.config,
      sipServiceConfig: !!sipServiceConfig,
      persistedConfig: !!persistedConfig,
    });
    return null;
  }

  /**
   * Cleanup all resources
   *
   * NOTE: We intentionally do NOT clear calls from the store here.
   * The Zustand store is global and should persist calls across Provider
   * remounts. Calls should only be cleared when explicitly ended.
   */
  async cleanup(): Promise<void> {
    // Re-use in-flight cleanup if already running (prevents redundant parallel teardown)
    if (this.cleanupPromise !== null) {
      return this.cleanupPromise;
    }

    const runCleanup = async () => {
      logger.debug('Cleaning up all resources...');

      if (this.unsubscribeActiveCalls) {
        this.unsubscribeActiveCalls();
        this.unsubscribeActiveCalls = null;
      }

      this.clearConnectionLossFeedback();

      // Stop heartbeat monitoring
      if (this.heartbeatService !== null) {
        try {
          this.heartbeatService.stop();
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          logger.error('Error during heartbeat cleanup', error);
        }
      }

      // Stop network monitoring
      if (this.networkMonitor !== null) {
        try {
          this.networkMonitor.cleanup();
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          logger.error('Error during network monitor cleanup', error);
        }
      }

      // Deactivate coordinator with null safety
      if (this.coordinator !== null) {
        try {
          this.coordinator.deactivate('new');
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          logger.error('Error during coordinator deactivation', error);
        }
      } else {
        logger.warn(
          'No coordinator configured, skipping coordinator deactivation'
        );
      }

      // NOTE: Do NOT call this.store?.clearAllCalls() here!
      // Calls should persist in the global Zustand store across Provider remounts.
      // They will be cleared when explicitly ended via endCall().

      // 1. Cleanup CallManager (store subscriptions and session listeners)
      if (this.callManager !== null) {
        try {
          this.callManager.cleanup();
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          logger.error('Error during CallManager cleanup', error);
        }
      }

      // 2. Cleanup WebRTC
      if (this.webrtcService !== null) {
        try {
          this.webrtcService.cleanupAll();
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          logger.error('Error during WebRTC cleanup', error);
        }
      }

      // 3. Cleanup audio
      if (this.audioService !== null) {
        try {
          this.audioService.cleanup();
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          logger.error('Error during AudioService cleanup', error);
        }
      }

      // Cleanup SipService (UserAgent and Registerer)
      // This must be done AFTER other services to ensure proper cleanup order
      // IMPORTANT: Wait for async cleanup to complete!
      if (this.sipService !== null) {
        try {
          logger.debug('Cleaning up SipService (async)...');
          await this.sipService.cleanup();
          logger.debug('SipService cleanup complete');
        } catch (e) {
          const error = e instanceof Error ? e : new Error(String(e));
          logger.error('Error during SipService cleanup', error);
        }
      }

      // Reset state flags AFTER all cleanup is complete
      this.isInitialized = false;

      logger.info('Cleanup complete');
    };

    this.cleanupPromise = runCleanup();
    await this.cleanupPromise;
    this.cleanupPromise = null;
  }

  /**
   * Handle SIP config fetch error
   */
  handleConfigError(error: unknown): void {
    if (!this.store) return;

    logger.error(
      'Failed to fetch SIP config',
      error instanceof Error ? error : new Error(String(error))
    );
    this.store.setStatus('failed');
    this.store.setError(
      error instanceof Error ? error : new Error(String(error))
    );
  }

  /**
   * Clear connection loss timers, UI state and audio.
   * Mirrors legacy PhoneStore.clearConnectionTimers() / clearConnectionLossFeedback().
   */
  clearConnectionLossFeedback(): void {
    if (this.connectionLossFeedbackTimer) {
      clearTimeout(this.connectionLossFeedbackTimer);
      this.connectionLossFeedbackTimer = null;
    }

    if (this.callDropTimer) {
      clearTimeout(this.callDropTimer);
      this.callDropTimer = null;
    }

    this.store?.setConnectionLossFeedbackActive(false);
    this.audioService.stopLostConnectionTone();
  }

  /**
   * Stop lost-connection feedback when the last active call ends.
   * Mirrors legacy behavior where connection-loss UI/audio is call-scoped.
   */
  private handleAllCallsEnded(): void {
    const store = useSoftphoneStore.getState();
    const hadConnectionIssue =
      store.isConnectionLossFeedbackActive || store.status === 'disconnected';

    this.clearConnectionLossFeedback();

    if (hadConnectionIssue) {
      pushToGTMDataLayer(
        ANALYTICS_EVENT_SOFTPHONE.CALL_ENDED_DURING_CONNECTION_ISSUE,
        { connection_status: 'off' }
      );
    }
  }

  /**
   * Subscribe to active call count — clear lost-connection feedback when no calls remain.
   */
  private setupActiveCallsSubscription(): void {
    if (this.unsubscribeActiveCalls) {
      return;
    }

    this.unsubscribeActiveCalls = useSoftphoneStore.subscribe(
      (state) => state.activeCalls.size,
      (activeCallCount, previousCount) => {
        if (previousCount > 0 && activeCallCount === 0) {
          this.handleAllCallsEnded();
        }
      }
    );
  }

  /**
   * Handle network loss with audio alert
   * Based on legacy PhoneStore.noInternetOnActiveCall()
   *
   * Mirrors legacy behavior: waits CONNECTION_LOSS_FEEDBACK_DELAY_MS (5s) before
   * showing the connection loss UI and playing the audio tone. This avoids false
   * positives from brief network blips.
   */
  private handleNetworkLossWithAlert(): void {
    if (!this.store) return;

    logger.warn('Network connection lost');
    this.store.setStatus('disconnected');

    // Reset any pending/active feedback before starting a new loss cycle (legacy: clearConnectionTimers)
    this.clearConnectionLossFeedback();

    const hasActiveCalls = useSoftphoneStore.getState().activeCalls.size > 0;
    if (hasActiveCalls) {
      this.connectionLossFeedbackTimer = setTimeout(() => {
        this.connectionLossFeedbackTimer = null;
        this.store?.setConnectionLossFeedbackActive(true);
        logger.warn('Active call detected, playing lost connection tone');
        this.audioService.playLostConnectionTone();

        // Analytics: Track lost connection detected and audio started
        pushToGTMDataLayer(
          ANALYTICS_EVENT_SOFTPHONE.LOST_CONNECTION_DETECTED,
          { has_active_call: true }
        );
        pushToGTMDataLayer(
          ANALYTICS_EVENT_SOFTPHONE.LOST_CONNECTION_AUDIO_STARTED,
          { has_active_call: true }
        );
      }, CONNECTION_LOSS_FEEDBACK_DELAY_MS);

      // Hard call-drop timeout — mirrors legacy PhoneStore.noInternetOnActiveCall() setTimeOut (13s).
      // If the network is not restored within CALL_DROP_TIMEOUT_MS, all active/incoming calls
      // are terminated via network termination (same as legacy cancelCallOnNoWebRTC()).
      // The timer is reset on each reconnection attempt to give a fresh window.
      this.startCallDropTimer();
    }
  }

  /**
   * Handle network restored with automatic reconnection
   * Based on legacy PhoneStore.handleNetworkRestored()
   *
   * KEY BEHAVIOUR: when an active call is in progress, we must NOT do a full
   * cleanup + reinit because that stops the UserAgent and terminates all SIP
   * sessions. Instead we use a lightweight transport-only reconnect that
   * mirrors the legacy PhoneStore.reconnectSip() path.
   */
  private async handleNetworkRestoredWithReconnect(): Promise<void> {
    if (!this.store) return;

    logger.info('Network connection restored');

    const hasActiveCalls = useSoftphoneStore.getState().activeCalls.size > 0;

    // Track if we're restoring from a connection issue
    const wasConnectionLost = this.store.isConnectionLossFeedbackActive;

    this.clearConnectionLossFeedback();

    // Analytics: Track connection restored if we were in a lost state
    if (wasConnectionLost) {
      pushToGTMDataLayer(ANALYTICS_EVENT_SOFTPHONE.CONNECTION_RESTORED, {
        from_status: 'off',
        has_active_call: hasActiveCalls,
      });
    }

    try {
      if (hasActiveCalls) {
        logger.info(
          'Active call detected — using lightweight transport reconnect to preserve call session'
        );
        // Reset the drop timer: give another CALL_DROP_TIMEOUT_MS window for the reconnect to
        // complete. Mirrors legacy attemptReconnection() which resets setTimeOut per attempt.
        // awaitReRegistration() will call clearConnectionLossFeedback() (which cancels this
        // timer) when registration is confirmed.
        this.startCallDropTimer();
        await this.reconnectTransportOnly();
      } else {
        logger.info(
          'No active calls — attempting full reconnection with retry after network restore'
        );
        await this.reconnectWithRetry();
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('Reconnection after network restore failed', error);
      this.store.setError(error);
    }
  }

  /**
   * Lightweight transport-only reconnect — preserves active SIP sessions.
   *
   * Mirrors legacy PhoneStore.reconnectSip(): only reconnects the WebSocket
   * transport without stopping the UserAgent or destroying SIP sessions.
   * When transport reconnects, SipService.setupTransportHandlers() onConnect
   * fires automatically and re-registers the Registerer if needed.
   *
   * Used when network restores during an active call so the call survives.
   * Falls back to full reconnect only when no UserAgent exists (which would
   * have terminated the call anyway).
   */
  private async reconnectTransportOnly(): Promise<void> {
    const ua = this.sipService.getUserAgent();

    if (!ua || !ua.transport) {
      // No UA means there is nothing to preserve — fall back to full reconnect.
      logger.warn('reconnectTransportOnly: no UserAgent available, falling back to full reconnect');
      await this.reconnectWithRetry();
      return;
    }

    const transportState = ua.transport.state;
    logger.info('reconnectTransportOnly: current transport state', { transportState });

    if (transportState === 'Connected') {
      // SIP.js auto-reconnected the transport (reconnectionAttempts > 0) before
      // our code ran. The store is still 'disconnected' (set on network loss), so
      // we must sync it here — otherwise the banner never clears.
      logger.info('reconnectTransportOnly: transport already Connected — SIP.js auto-reconnected, syncing store');
      this.awaitReRegistration();
      return;
    }

    try {
      this.store?.setStatus('connecting');
      logger.info('reconnectTransportOnly: calling transport.connect()...');
      await ua.transport.connect();
      // onConnect in SipService.setupTransportHandlers() will re-register automatically.
      // awaitReRegistration() watches the Registerer state and updates the store when
      // registration completes — clearing the connection-loss banner.
      logger.info('reconnectTransportOnly: transport reconnected, SIP session preserved');
      this.awaitReRegistration();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      logger.error('reconnectTransportOnly: transport.connect() failed', error);
      this.store?.setStatus('failed');
      // Don't escalate to full reconnect here: that would stop the UA and terminate
      // the call. The heartbeat will retry on the next 15s tick.
      throw error;
    }
  }

  /**
   * Start (or restart) the hard call-drop timer.
   * Mirrors legacy PhoneStore.noInternetOnActiveCall() / attemptReconnection() setTimeOut (13s).
   * Clears any existing timer first so callers can safely reset it on each attempt.
   */
  private startCallDropTimer(): void {
    if (this.callDropTimer) {
      clearTimeout(this.callDropTimer);
      this.callDropTimer = null;
    }

    const delayMs = CALL_DROP_TIMEOUT_MS as number;

    this.callDropTimer = setTimeout(() => {
      this.callDropTimer = null;
      logger.warn('Call-drop timeout reached — terminating all calls (network not restored in time)');
      void this.dropAllCallsOnNetworkTimeout();
    }, delayMs);
  }

  /**
   * Terminate all active and incoming calls due to network timeout.
   * Mirrors legacy PhoneStore.cancelCallOnNoWebRTC():
   *   - stops lost-connection tone
   *   - sends BYE / cancel for every session (fire-and-forget — network may be down)
   *   - clears store call state
   */
  private async dropAllCallsOnNetworkTimeout(): Promise<void> {
    logger.warn('dropAllCallsOnNetworkTimeout: terminating all calls due to network timeout');

    // Stop the lost-connection tone and hide the overlay
    this.clearConnectionLossFeedback();

    const store = useSoftphoneStore.getState();
    const activeCallIds = Array.from(store.activeCalls.keys());
    const incomingCallIds = Array.from(store.incomingCalls.keys());

    // Hang up each active call — fire-and-forget; BYE may not reach server if network is down
    for (const callId of activeCallIds) {
      try {
        await this.callManager.hangUp(callId);
      } catch (err) {
        logger.warn('dropAllCallsOnNetworkTimeout: hangUp failed for call', { callId, err });
      }
    }

    // Decline each incoming call
    for (const callId of incomingCallIds) {
      try {
        await this.callManager.hangUp(callId);
      } catch (err) {
        logger.warn('dropAllCallsOnNetworkTimeout: hangUp failed for incoming call', { callId, err });
      }
    }

    // Safety net — ensure store is cleared even if hangUp had errors
    useSoftphoneStore.getState().clearAllCalls();

    this.audioService.stopAllCallAudio();
  }

  /**
   * After a transport-only reconnect, watches the Registerer state change and
   * updates the store to 'registered' when SIP re-registration succeeds.
   * This clears the connection-loss banner without touching the SIP session.
   */
  private awaitReRegistration(): void {
    const registerer = this.sipService.getRegisterer();
    if (!registerer) {
      logger.warn('awaitReRegistration: no registerer found');
      return;
    }

    if (registerer.state === RegistererState.Registered) {
      this.store?.setStatus('registered');
      this.store?.setError(null);
      this.clearConnectionLossFeedback();
      return;
    }

    const listener = (state: RegistererState) => {
      if (state === RegistererState.Registered) {
        this.store?.setStatus('registered');
        this.store?.setError(null);
        this.clearConnectionLossFeedback();
        registerer.stateChange.removeListener(listener);
      } else if (state === RegistererState.Terminated) {
        logger.warn('awaitReRegistration: registerer terminated after transport reconnect');
        registerer.stateChange.removeListener(listener);
      }
    };

    registerer.stateChange.addListener(listener);
  }
}
